/**
 * PoolScopeAssignBanner — shown on an Item Pool that has no cycle and no track.
 *
 * Doc 03 §2.11A: an Item Pool's Track / Cycle scope is required at lock. A
 * draft may be assembled unscoped; this is where it gets its home before it
 * can lock or feed a session. Writes go through updateItemPool (lists.edit).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Link2, Loader2 } from 'lucide-react';
import { PoolScopeFields } from '../PoolScopeFields';
import { useItemPoolsStore } from '../../../stores/prioritization/itemPoolsStore';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { useTrackStore } from '../../../stores/prioritization/trackStore';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import {
  findPoolOnTrack,
  poolPath,
  resolvePoolScope,
  trackAlreadyHasPoolMessage,
  type PoolScopeKey,
} from '../../../lib/poolScope';
import type { ItemPool } from '../../../types/prioritization/lifecycle';

interface PoolScopeAssignBannerProps {
  pool: ItemPool;
  canEdit: boolean;
}

export function PoolScopeAssignBanner({ pool, canEdit }: PoolScopeAssignBannerProps) {
  const updateItemPool = useItemPoolsStore((s) => s.updateItemPool);
  const itemPools = useItemPoolsStore((s) => s.itemPools);
  const cycles = useDegapProcessStore((s) => s.cycles);
  const fetchCycles = useDegapProcessStore((s) => s.fetchFromSupabase);
  const tracks = useTrackStore((s) => s.tracks);
  const fetchAllTracks = useTrackStore((s) => s.fetchAllTracks);
  const addNotification = useUIStore((s) => s.addNotification);

  const [scopeKey, setScopeKey] = useState<PoolScopeKey>('');
  const [trackId, setTrackId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (cycles.length === 0) void fetchCycles();
    if (tracks.length === 0) void fetchAllTracks();
    // Load once on mount; the stores refetching on every render would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scope = resolvePoolScope(scopeKey, tracks, trackId);
  // One pool per track: refuse, and point at the pool that is already there.
  const poolOnTrack = useMemo(
    () => findPoolOnTrack(itemPools ?? [], scope?.trackId, pool.id),
    [itemPools, scope?.trackId, pool.id],
  );
  const canAssign = canEdit && !!scope && (!!scope.cycleId || !!scope.trackId) && !poolOnTrack && !saving;

  const handleAssign = useCallback(async () => {
    if (!scope || (!scope.cycleId && !scope.trackId) || poolOnTrack) return;
    setSaving(true);
    try {
      // Say "assigned" only once the save has actually happened.
      const result = await updateItemPool(pool.id, scope as Partial<ItemPool>);
      if (result.success) {
        addNotification({
          type: 'success',
          title: 'Pool assigned',
          message: 'This pool now has a home and can be locked when ready.',
        });
      } else {
        addNotification({
          type: 'error',
          title: 'Pool not assigned',
          message: result.error ?? 'The assignment could not be saved. Nothing was changed.',
          duration: 8000,
        });
      }
    } finally {
      setSaving(false);
    }
  }, [scope, poolOnTrack, updateItemPool, pool.id, addNotification]);

  return (
    <div className="border border-[var(--mustard)] bg-[var(--mustard-dim)] px-4 py-3 space-y-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 mt-0.5 text-[var(--mustard)] shrink-0" />
        <div className="text-sm text-[var(--text-pri)]">
          <p className="font-medium">This pool has no cycle or track yet.</p>
          <p className="text-xs text-[var(--text-sec)] mt-0.5">
            You can add and arrange items now. It cannot be locked or ranked until it has a home — the criteria set and the sessions live on the track.
          </p>
        </div>
      </div>
      {canEdit && (
        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] items-end">
          <PoolScopeFields
            cycles={cycles}
            tracks={tracks}
            scopeKey={scopeKey}
            trackId={trackId}
            onScopeKeyChange={setScopeKey}
            onTrackIdChange={setTrackId}
            disabled={saving}
            idPrefix="assign-pool"
          />
          <button
            type="button"
            onClick={() => void handleAssign()}
            disabled={!canAssign}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
            Assign
          </button>
        </div>
      )}
      {canEdit && poolOnTrack && (
        <p className="text-sm text-[var(--coral)]" role="alert">
          {trackAlreadyHasPoolMessage(poolOnTrack.name)}.{' '}
          <Link to={poolPath(poolOnTrack.id)} className="underline underline-offset-2">
            Open it
          </Link>{' '}
          or choose a different track.
        </p>
      )}
    </div>
  );
}
